/**
 * Abrechnung je Unternehmen (Spec 2026-10-21): Status, Weg in den Schritt
 * „Abrechnung einrichten“, Vorbelegung der Rechnungsadresse, die Frage,
 * welche kostenpflichtigen Apps im Unternehmen noch ein Abo brauchen, und
 * die Meldungen zum Abo-Start der Unternehmen nach einer Buchung.
 */
import type { CompanySkip, OwnBillingOutcome, PaymentDueItem } from '@/services/appsApi'
import { gt } from '@/i18n/gt'
import { postRequestGeneric } from '@/services/backendApiGeneric'
import { type CompanyBillingStatus, getCompanyBillingStatuses } from '@/services/billingApi'

/**
 * Spiegel von subscriptionExists im Backend (company_subscription.go): In
 * diesen Zuständen besteht ein Abo und deckt seine App ab. Auch ein
 * pausiertes Abo (Testphase ohne Zahlungsmittel beendet) zählt; es wird nicht
 * neu gebucht, sondern mit einem Zahlungsmittel fortgesetzt.
 */
const EXISTING_SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set(['trialing', 'active', 'past_due', 'paused'])

/** Meldet, ob ein Abo in diesem Zustand besteht (Buchungsentscheidungen). */
export function subscriptionExists (status: string): boolean {
  return EXISTING_SUBSCRIPTION_STATUSES.has(status)
}

/**
 * Apps, deren Abo im Scope pausiert ist und für die dort kein anderes
 * bestehendes Abo läuft (Review M10): Organisations-Scope (idCompany null)
 * nur organisationsweite Abos, Unternehmens-Scope nur die des Unternehmens.
 * Für sie zeigt die Oberfläche „Abo pausiert – bitte Zahlungsmittel
 * hinterlegen.“ statt einer neuen Buchung.
 */
export function pausedSubscriptionApps (
  subscriptions: readonly { appKey: string, status: string, idCompany?: number | null }[],
  idCompany: number | null = null,
): Set<string> {
  const paused = new Set<string>()
  const running = new Set<string>()
  for (const s of subscriptions) {
    if ((s.idCompany ?? null) !== idCompany) {
      continue
    }
    if (s.status === 'paused') {
      paused.add(s.appKey)
    } else if (subscriptionExists(s.status)) {
      running.add(s.appKey)
    }
  }
  return new Set([...paused].filter(appKey => !running.has(appKey)))
}

/**
 * Ergebnis von loadCompanyBillingStatuses. ok=false heißt: Status unbekannt.
 * Aufrufer dürfen dann nichts als „abgedeckt“ oder „eingerichtet“ werten,
 * sondern zeigen error an bzw. fallen auf die sichere Seite zurück.
 */
export type CompanyBillingStatusLoad
  = | { ok: true, statuses: CompanyBillingStatus[] }
    | { ok: false, statuses: CompanyBillingStatus[], error: string }

/** Status aller Unternehmen (oder nur von idCompany); Fehler werden gemeldet, nicht verschluckt. */
export async function loadCompanyBillingStatuses (idCompany?: number | null): Promise<CompanyBillingStatusLoad> {
  try {
    const list = await (idCompany ? getCompanyBillingStatuses(idCompany) : getCompanyBillingStatuses())
    return { ok: true, statuses: Array.isArray(list) ? list : [] }
  } catch (error) {
    const message = (error as { message?: unknown } | null)?.message
    return {
      ok: false,
      statuses: [],
      error: typeof message === 'string' && message.trim()
        ? message
        : gt('Der Abrechnungsstatus konnte nicht geladen werden.'),
    }
  }
}

export function statusOf (statuses: CompanyBillingStatus[], idCompany: number | null | undefined): CompanyBillingStatus | null {
  if (idCompany == null) {
    return null
  }
  return statuses.find(s => s.idCompany === idCompany) ?? null
}

/**
 * Status genau eines Unternehmens aus einer Ladeantwort. Fehlt das
 * Unternehmen in einer erfolgreichen Antwort, gilt der Status als unbekannt
 * (fail-closed) – nicht als „nicht eingerichtet“ oder „organisationsweit“.
 */
export function companyStatusFromLoad (
  result: CompanyBillingStatusLoad,
  idCompany: number,
): { status: CompanyBillingStatus | null, error: string } {
  if (!result.ok) {
    return { status: null, error: result.error }
  }
  const status = statusOf(result.statuses, idCompany)
  return status
    ? { status, error: '' }
    : { status: null, error: gt('Der Abrechnungsstatus konnte nicht geladen werden.') }
}

/** Eigene Abrechnung nur bei bekanntem Status mit billedViaOrganization=false. */
export function isOwnBilling (statuses: CompanyBillingStatus[], idCompany: number | null | undefined): boolean {
  return statusOf(statuses, idCompany)?.billedViaOrganization === false
}

/** Unternehmen mit eigener Abrechnung, die noch nicht eingerichtet sind. */
export function needsBillingSetup (statuses: CompanyBillingStatus[], idCompany: number | null | undefined): boolean {
  const status = statusOf(statuses, idCompany)
  return !!status && !status.billingSetUp
}

/** Abschnitt „Abrechnung“ im Unternehmensbereich der Organisationsseite. */
export function billingSetupRoute (idCompany: number) {
  return {
    name: 'PageOrganization',
    query: { bereich: 'unternehmen', unternehmen: String(idCompany), abschnitt: 'abrechnung' },
  }
}

export interface BillingPrefill {
  companyName: string
  email: string
  line1: string
  postalCode: string
  city: string
}

/** Vorbelegung der Rechnungsadresse aus Firmenname und Hauptstandort. */
export function billingPrefill (
  company: { name?: unknown, email?: unknown } | null,
  location: { streetNo?: unknown, zip?: unknown, city?: unknown } | null,
): BillingPrefill {
  const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
  return {
    companyName: text(company?.name),
    email: text(company?.email),
    line1: text(location?.streetNo),
    postalCode: text(location?.zip),
    city: text(location?.city),
  }
}

export interface ScopedSubscription {
  appKey: string
  idCompany: number | null
  status: string
  stripeSubscriptionId: string | null
}

/** Paywall-Befreiung wie im Grid (paywallExempt = alle abrechenbaren Apps). */
export interface AppExemption {
  paywallExempt: boolean
  exemptApps: readonly string[]
}

/**
 * Kostenpflichtige Apps der Auswahl ohne passendes lebendes Abo. Passend ist
 * das Abo des Unternehmens des Standorts oder das organisationsweite Abo,
 * wenn das Unternehmen nachweislich darüber abgerechnet wird
 * (billedViaOrganization === true im Status), oder eine Freilizenz ohne
 * Stripe-Abo. Spiegel von billing.subscriptionsInScope im Backend.
 * Fehlt der Status eines Unternehmens, zählt das Organisations-Abo nicht
 * (fail-closed). Standorte ohne bekanntes Unternehmen überspringt die
 * Prüfung wie companiesNeedingBillingSetup im Backend.
 */
export function uncoveredBillableApps (
  selection: { appKey: string, idLocation: number }[],
  billableApps: ReadonlySet<string>,
  companyOfLocation: ReadonlyMap<number, number>,
  statuses: CompanyBillingStatus[],
  subscriptions: ScopedSubscription[],
  exemption: AppExemption,
): Set<string> {
  const out = new Set<string>()
  if (exemption.paywallExempt) {
    return out
  }
  const exemptApps = new Set(exemption.exemptApps)
  const viaOrganization = new Map(statuses.map(s => [s.idCompany, s.billedViaOrganization]))
  for (const { appKey, idLocation } of selection) {
    if (!billableApps.has(appKey) || exemptApps.has(appKey) || out.has(appKey)) {
      continue
    }
    const idCompany = companyOfLocation.get(idLocation)
    if (idCompany === undefined) {
      continue
    }
    const organizationCovers = viaOrganization.get(idCompany) === true
    const covered = subscriptions.some(s => s.appKey === appKey && subscriptionExists(s.status) && (
      s.idCompany === null
        ? organizationCovers || s.stripeSubscriptionId === null
        : s.idCompany === idCompany
    ))
    if (!covered) {
      out.add(appKey)
    }
  }
  return out
}

export interface SkippedCompanyMessage extends CompanySkip {
  name: string
  /** Deutscher Grund für die Anzeige. */
  text: string
}

export interface CheckoutOutcomeMessages {
  /** Unternehmen, deren Abos diese Buchung gestartet hat. */
  startedNames: string[]
  skipped: SkippedCompanyMessage[]
}

function skipReasonText (reason: string): string {
  switch (reason) {
    case 'billing_not_set_up': {
      return gt('Die Abrechnung ist noch nicht eingerichtet. Bitte hinterlegen Sie Rechnungsadresse und Zahlungsmittel des Unternehmens.')
    }
    case 'no_payment_method': {
      return gt('Kein Zahlungsmittel hinterlegt. Die Apps dieses Unternehmens wurden nicht gebucht.')
    }
    case 'busy': {
      return gt('Die Buchung wird gerade verarbeitet. Bitte laden Sie die Seite in einigen Sekunden neu.')
    }
    default: {
      return gt('Die Abos konnten nicht angelegt werden. Bitte versuchen Sie es später erneut.')
    }
  }
}

/**
 * Meldungen zum Abo-Start der Unternehmen mit eigener Abrechnung nach einer
 * Buchung im Organisations-Scope (POST /apps/checkout bzw. checkout-session).
 */
export function describeCheckoutOutcome (
  outcome: Partial<OwnBillingOutcome>,
  statuses: { idCompany: number, name: string }[],
): CheckoutOutcomeMessages {
  const names = new Map(statuses.map(s => [s.idCompany, s.name]))
  const nameOf = (id: number) => names.get(id) || `${gt('Unternehmen')} ${id}`
  return {
    startedNames: (outcome.startedCompanies ?? []).map(id => nameOf(id)),
    skipped: (outcome.skipped ?? []).map(s => ({ ...s, name: nameOf(s.idCompany), text: skipReasonText(s.reason) })),
  }
}

/** Eine Snackbar-Meldung für das Ergebnis (der Store zeigt nur eine zugleich). */
export function checkoutOutcomeSnackbar (
  messages: CheckoutOutcomeMessages,
): { message: string, type: 'success' | 'warning' } | null {
  const started = messages.startedNames.length > 0
    ? `${gt('Abos gestartet für:')} ${messages.startedNames.join(', ')}`
    : ''
  if (messages.skipped.length > 0) {
    const skipped = messages.skipped.map(s => `„${s.name}“: ${s.text}`).join(' ')
    const head = `${gt('Nicht alle Unternehmen konnten gebucht werden.')} ${skipped}`
    return { type: 'warning', message: started ? `${started}. ${head}` : head }
  }
  return started ? { type: 'success', message: started } : null
}

/** Formular „Hauptstandort“ im Schritt „Abrechnung einrichten“ (Entscheidung 13). */
export interface MainLocationForm {
  name: string
  streetNo: string
  zip: string
  city: string
}

const MAIN_LOCATION_FIELDS: (keyof MainLocationForm)[] = ['name', 'streetNo', 'zip', 'city']

/** Leere Pflichtfelder des Hauptstandorts. */
export function missingMainLocationFields (form: MainLocationForm): (keyof MainLocationForm)[] {
  return MAIN_LOCATION_FIELDS.filter(field => !form[field].trim())
}

/** Nutzlast für POST /protected/locations/create/{idCompany}, wie Organigramm und Onboarding. */
export function mainLocationPayload (idCompany: number, form: MainLocationForm) {
  return {
    idCompany,
    name: form.name.trim(),
    streetNo: form.streetNo.trim(),
    zip: form.zip.trim(),
    city: form.city.trim(),
    country: 'Deutschland',
  }
}

/**
 * Legt den Hauptstandort über den bestehenden Standort-Endpunkt an und
 * liefert seine ID. Der erste Standort eines Unternehmens wird im Backend
 * dessen Hauptstandort.
 */
export async function createMainLocation (idCompany: number, form: MainLocationForm): Promise<number> {
  const resp = await postRequestGeneric(
    `/protected/locations/create/${idCompany}`,
    mainLocationPayload(idCompany, form),
    true,
  ) as { id?: number } | null
  return Number(resp?.id) || 0
}

export { type CompanyBillingStatus } from '@/services/billingApi'

/**
 * Apps der Auswahl, die an mindestens einem Standort nur ein pausiertes Abo
 * abdeckt: dort mit pausierten Abos abgedeckt, ohne sie nicht (gleiche
 * Scope-Regeln wie die Buchung, uncoveredBillableApps). Verglichen wird je
 * Standort (und damit je Unternehmen), erst danach je App zusammengefasst:
 * Ist KARS in A nur pausiert abgedeckt und in B gar nicht, bleibt der
 * Hinweis für A (Review Copilot #1157).
 */
export function pausedCoveredApps (
  selection: { appKey: string, idLocation: number }[],
  billableApps: ReadonlySet<string>,
  companyOfLocation: ReadonlyMap<number, number>,
  statuses: CompanyBillingStatus[],
  subscriptions: ScopedSubscription[],
  exemption: AppExemption,
): Set<string> {
  const running = subscriptions.filter(s => s.status !== 'paused')
  const out = new Set<string>()
  for (const entry of selection) {
    if (out.has(entry.appKey)) {
      continue
    }
    const one = [entry]
    const withoutPaused = uncoveredBillableApps(one, billableApps, companyOfLocation, statuses, running, exemption)
    const withPaused = uncoveredBillableApps(one, billableApps, companyOfLocation, statuses, subscriptions, exemption)
    if (withoutPaused.has(entry.appKey) && !withPaused.has(entry.appKey)) {
      out.add(entry.appKey)
    }
  }
  return out
}

/** Was die Oberfläche zu pausierten Abos zeigt (Review M10). */
export interface PaymentNotices {
  /** Offene Rechnungen: „Zahlung erforderlich“ mit „Rechnung öffnen“. */
  due: PaymentDueItem[]
  /** „Abo pausiert – bitte Zahlungsmittel hinterlegen.“ */
  showPausedHint: boolean
}

/**
 * Offene Rechnungen haben Vorrang: Für eine App mit offener Rechnung
 * erscheint kein Zahlungsmittel-Hinweis; er erscheint nur für pausierte
 * Apps ohne offene Rechnung.
 */
export function paymentNotices (pausedApps: ReadonlySet<string>, due: readonly PaymentDueItem[]): PaymentNotices {
  const dueApps = new Set(due.map(d => d.appKey))
  return {
    due: [...due],
    showPausedHint: [...pausedApps].some(appKey => !dueApps.has(appKey)),
  }
}
